import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { SITE_URL } from "@/lib/seoMeta";
import { IMAGES } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";
import { loc, catLabel } from "@/lib/localize";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { ArticleCard } from "@/components/common/ArticleCard";
import { Button } from "@/components/ui/button";
import { Clock, User, Calendar, Share2, AlertTriangle } from "lucide-react";

function fmt(d) {
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; }
}

export default function Article() {
  const { slug } = useParams();
  const { L, isAr } = useLang();
  const { data: res, isLoading: loading, isError: err } = useQuery({
    queryKey: ["article-full", slug],
    queryFn: async () => (await api.get(`/articles/${slug}`)).data,
    retry: false,
  });
  const a = res?.article;
  useSeo({
    title: a && (loc(a, "seo_title", isAr) || loc(a, "title", isAr)),
    description: a && (loc(a, "seo_description", isAr) || loc(a, "excerpt", isAr)),
    image: a?.featured_image || IMAGES.skyline,
    canonical: `${SITE_URL}/market-analysis/${slug}`,
    locale: isAr ? "ar_AE" : "en_US",
  });

  if (loading) return <div className="container-tight py-32 text-center text-subtle">...</div>;
  if (err || !a) return (
    <div className="container-tight py-32 text-center">
      <h1 className="font-heading text-2xl font-semibold text-ink">404</h1>
      <Button asChild className="mt-6 bg-finblue hover:bg-finblueDark"><Link to="/market-analysis">{L.insights.viewAll}</Link></Button>
    </div>
  );

  const share = () => {
    if (navigator.share) navigator.share({ title: loc(a, "title", isAr), url: window.location.href });
    else navigator.clipboard.writeText(window.location.href);
  };

  return (
    <article>
      <div className="container-tight pt-10">
        <Breadcrumbs items={[{ label: L.common.home, to: "/" }, { label: L.pages.analysis.overline, to: "/market-analysis" }, { label: loc(a, "title", isAr) }]} />
      </div>

      <header className="container-tight max-w-3xl">
        <span className="overline">{catLabel(a.category, isAr)}</span>
        <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-ink lg:text-4xl">{loc(a, "title", isAr)}</h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-subtle">
          <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> {a.author}</span>
          <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {fmt(a.published_at)}</span>
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {a.reading_time} {L.common.min}</span>
          <button onClick={share} className="flex items-center gap-1.5 text-finblue" data-testid="article-share"><Share2 className="h-4 w-4" /> {L.common.search === "بحث" ? "مشاركة" : "Share"}</button>
        </div>
      </header>

      <div className="container-tight mt-8 overflow-hidden rounded-2xl">
        <img src={a.featured_image || "https://images.unsplash.com/photo-1651341050677-24dba59ce0fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"} alt={loc(a, "title", isAr)} className="h-64 w-full rounded-2xl border border-hair object-cover lg:h-96" />
      </div>

      <div className="container-tight mt-12 grid max-w-6xl gap-12 lg:grid-cols-[1fr_260px]">
        <div>
          <div className="article-body" dangerouslySetInnerHTML={{ __html: loc(a, "body", isAr) }} />
          {a.instruments?.length > 0 && (
            <div className="mt-8 rounded-lg border border-hair bg-canvas p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-subtle">{isAr ? "أدوات ذات صلة" : "Relevant instruments"}</p>
              <div className="mt-2 flex flex-wrap gap-2">{a.instruments.map((i) => <span key={i} className="rounded-md border border-hair bg-white px-2.5 py-1 text-sm text-ink">{i}</span>)}</div>
            </div>
          )}
          <div className="mt-8 flex gap-3 rounded-lg border border-hair bg-canvas p-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            <p className="text-xs leading-relaxed text-subtle">{isAr ? "هذا المحتوى لأغراض تعليمية فقط ولا يشكّل نصيحة استثمارية. ينطوي التداول على مخاطر كبيرة." : "This content is for educational purposes only and does not constitute investment advice. Trading involves significant risk."}</p>
          </div>
        </div>

        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-xl border border-hair bg-navy p-6 text-white">
            <h3 className="font-heading text-base font-semibold">{isAr ? "ادخل الأسواق" : "Access the markets"}</h3>
            <Button asChild className="mt-4 w-full bg-finblue hover:bg-finblueDark"><Link to="/broker-partners">{L.common.allPartners}</Link></Button>
          </div>
        </aside>
      </div>

      {res?.related?.length > 0 && (
        <section className="mt-20 bg-canvas py-16">
          <div className="container-tight max-w-6xl">
            <h2 className="font-heading text-2xl font-semibold text-ink">{isAr ? "مقالات ذات صلة" : "Related articles"}</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">{res.related.map((r) => <ArticleCard key={r.id} article={r} />)}</div>
          </div>
        </section>
      )}
    </article>
  );
}
